import { useMutation } from "@tanstack/react-query";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import type { AiPrintoutSettings } from "../pages/DaySchedule/printoutAiSettings";
import { backendService } from "../services/backend.service";
import { useFeatureFlag } from "./useOpenFeatureFlag";

export type PrintoutAiSettingsVariables = {
	/** What the user typed. Empty is allowed as long as there is an image. */
	prompt: string;
	/** An example printout the user attached, uploaded first so the model can look at it. */
	file: File | null;
	/** The settings the panel is showing right now — the model edits these rather than starting blank. */
	currentSettings: AiPrintoutSettings;
};

export type PrintoutAiSettingsResult = {
	/** The whole merged document. Present for completeness; prefer `changed` when applying. */
	settings: AiPrintoutSettings;
	/**
	 * Only the fields the model deliberately set.
	 *
	 * `settings` is merged server-side onto the settings sent at submit time, so applying it whole
	 * would revert anything the user changed while the request was in flight. Applying `changed`
	 * over the live settings cannot.
	 */
	changed: Partial<AiPrintoutSettings>;
	/** One human sentence describing what changed — the only feedback that anything happened. */
	summary: string | null;
};

/**
 * Turns a free-text request (and/or an example image) into Detailed printout settings.
 *
 * Two calls, one mutation: an attached image is uploaded first and referenced by its returned key, so
 * the configure request stays JSON and the image is never re-sent on a retry of the same attachment.
 * Errors are deliberately left to propagate — the caller surfaces the backend's `UIVisibleError` key
 * through `showErrorSnackbar` and reports it to analytics.
 */
export const usePrintoutAiSettings = () => {
	const { chosenDepartment } = useChosenDepartment();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	return useMutation<PrintoutAiSettingsResult, unknown, PrintoutAiSettingsVariables>({
		mutationFn: async ({ prompt, file, currentSettings }) => {
			const departmentId = chosenDepartment.department_id;
			const fileKey = file ? (await backendService.uploadPrintoutExampleFile(departmentId, file)).data.fileKey : null;
			const trimmedPrompt = prompt.trim();
			const { data } = await backendService.configurePrintoutSettingsWithAi({
				departmentId,
				prompt: trimmedPrompt.length > 0 ? trimmedPrompt : null,
				fileKey,
				currentSettings,
				jobManagementEnabled,
			});
			return data;
		},
	});
};
